import { Record } from "@/domain/records/entities/Record";
import { RecordStatus } from "@/domain/records/types";
import { ReportMetadata } from "@/domain/records/entities/ReportMetadata";

/**
 * Domain Service for Record business logic
 * Clean Architecture - Domain Layer
 */
export class RecordDomainService {
  /**
   * Determine if record can be processed for generation
   */
  canGenerateReports(record: Record): boolean {
    return (
      record.status === RecordStatus.Finished &&
      record.availableVersions &&
      Object.keys(record.availableVersions).length > 0
    );
  }

  /**
   * Check if record is in a valid state for viewing
   */
  canViewRecord(record: Record): boolean {
    return !record.hasError && record.name.trim().length > 0;
  }

  /**
   * Calculate completion percentage for record processing
   */
  getCompletionPercentage(record: Record): number {
    switch (record.status) {
      case RecordStatus.Finished:
        return 100;
      case RecordStatus.Processing:
      case RecordStatus.InProgress:
        return 50;
      case RecordStatus.Uploading:
        return 25;
      case RecordStatus.AudioSaved:
        return 10;
      case RecordStatus.Error:
        return 0;
      default:
        return 0;
    }
  }

  /**
   * Get user-friendly status message
   */
  getStatusMessage(record: Record): string {
    switch (record.status) {
      case RecordStatus.AudioSaved:
        return "File uploaded, ready for processing";
      case RecordStatus.Uploading:
        return "Uploading file...";
      case RecordStatus.Processing:
        return "Processing audio/video...";
      case RecordStatus.InProgress:
        return "Generating insights...";
      case RecordStatus.Finished:
        return "Processing complete";
      case RecordStatus.Error:
        return "Processing failed";
      default:
        return "Unknown status";
    }
  }

  /**
   * Check if record supports specific feature
   */
  supportsFeature(record: Record, feature: string): boolean {
    switch (feature) {
      case "transcript":
        return record.fileType !== "text";
      case "reports":
        return (
          record.availableVersions &&
          Object.keys(record.availableVersions).length > 0
        );
      case "editing":
        return record.status === RecordStatus.Finished;
      case "sharing":
        return record.status === RecordStatus.Finished;
      default:
        return false;
    }
  }

  /**
   * Create report metadata from record
   */
  createReportMetadata(record: Record): ReportMetadata {
    return ReportMetadata.fromAvailableVersions(
      record.id,
      record.availableVersions
    );
  }

  /**
   * Validate record data integrity
   */
  validateRecordIntegrity(record: Record): {
    isValid: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    if (!record.id || record.id.trim().length === 0) {
      issues.push("Record ID is missing");
    }

    if (!record.name || record.name.trim().length === 0) {
      issues.push("Record name is missing");
    }

    if (record.sizeMb <= 0) {
      issues.push("Invalid file size");
    }

    if (!record.availableVersions) {
      issues.push("Available versions data is missing");
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Get records sorted by priority (finished first, then by date)
   */
  sortRecordsByPriority(records: Record[]): Record[] {
    return [...records].sort((a, b) => {
      // Finished records first
      if (a.isFinished && !b.isFinished) return -1;
      if (!a.isFinished && b.isFinished) return 1;

      // Then by creation date (newest first)
      const aDate = a.createdAt || new Date(0);
      const bDate = b.createdAt || new Date(0);
      return bDate.getTime() - aDate.getTime();
    });
  }
}
